import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { mergeLeft, prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { NOTIFICATION_CENTER_ROLES } from 'app/constants'
import FileUploadProgress from 'app/plugins/openstack/components/images/FileUploadProgress'
import SupportBundleProgressPopover from 'app/plugins/openstack/components/infrastructure/hosts/SupportBundleProgressPopover'
import ISOMountProgressPopover from 'app/plugins/openstack/components/vms/ISOMountProgressPopover'
import { type ClientState, clientStoreKey } from 'core/client/clientReducers'
import NotificationBar from 'core/components/notification-bar/NotificationBar'
import { COLLAPSED_BAR_HEIGHT } from 'core/components/notification-bar/NotificationBarDrawer'
import { useSetNotificationBarOffset } from 'core/components/notification-bar/NotificationBarOffsetContext'
import Header from 'core/elements/header/Header'
import Sidebar from 'core/elements/sidebar'
import { getSections, renderMainContent } from 'core/plugins/helpers'
import pluginManager from 'core/plugins/pluginManager'
import { Route } from 'core/plugins/route'
import FrameContext, {
  type IFrameContextRefs,
  type IFullFrameContext,
} from 'core/providers/frame-provider'
import { permissionsKey } from 'core/session/permissionsReducers'
import type Theme from 'core/themes/model'
import { devEnabled } from 'core/utils/helpers'
import GlobalClusterDropdown from 'k8s/components/GlobalClusterDropdown'

interface Props {
  role: string
  features: any
  currentPluginId: string
  setPluginId: (id: string) => void
}

const contentTopHeaderRef = React.createRef<HTMLDivElement>()
const headerActionRef = React.createRef<HTMLDivElement>()
const headerRefreshRef = React.createRef<HTMLDivElement>()

const MainContent = ({ plugins, role, sidebarPane }) => {
  const classes = useStyles()
  const { setFrameContainerRef } = React.useContext(FrameContext)
  const permissions = useSelector(prop(permissionsKey))
  useEffect(() => {
    setFrameContainerRef({
      headerPrimaryActionContainer: headerActionRef.current,
      headerRefreshContainer: headerRefreshRef.current,
      contentTopHeaderContainer: contentTopHeaderRef.current,
    })
  }, [])

  const hasTopHeaderContent = contentTopHeaderRef?.current?.childElementCount
  const hasHeaderContent =
    headerRefreshRef?.current?.childElementCount || headerActionRef?.current?.childElementCount
  const route = Route.getCurrentRoute(location.pathname)
  const showClusterDropdown = route?.defaultParams?.showClusterDropdown === 'true'

  return (
    <section className={clsx('content-main', classes.contentMain)}>
      <div className={clsx(classes.contentTopHeader, hasTopHeaderContent && classes.headerSpacing)}>
        <div ref={contentTopHeaderRef} />
      </div>
      <div className={clsx(classes.headerContainer, hasHeaderContent && classes.headerSpacing)}>
        <div className={classes.headerActions}>
          {showClusterDropdown && <GlobalClusterDropdown />}
          <div ref={headerRefreshRef} />
        </div>
        <div className={clsx('header-actions', classes.headerActions)} ref={headerActionRef} />
      </div>
      {renderMainContent(plugins, role, devEnabled(), permissions)}
      <FileUploadProgress />
      <ISOMountProgressPopover />
      <SupportBundleProgressPopover />
    </section>
  )
}

function DefaultFrame({ currentPluginId, features, role }: Props) {
  const { history, location } = useReactRouter()
  const { frame: { sidebarPane = 'default' } = {} } = useSelector(
    prop<string, ClientState>(clientStoreKey),
  )
  const permissions = useSelector(prop(permissionsKey))
  const [frameRefs, setFrameRefs] = useState<IFullFrameContext>({} as any)
  const setDefaultFrameContainerRef = useCallback(
    (payload: Partial<IFrameContextRefs>) => setFrameRefs((frames) => mergeLeft(frames, payload)),
    [frameRefs],
  )

  const plugins = pluginManager.getPlugins()
  const classes = useStyles()

  const sections = getSections(plugins, role, features, permissions)
  // NB-FR-001: Admins only, and not on the Notification Center page itself
  const showNotificationBar =
    NOTIFICATION_CENTER_ROLES.includes(role) &&
    Route.getCurrentRoute(location.pathname)?.id !== 'notificationCenter'
  // NB-FR-001: Overlays fixed to the bottom of the window stay above the bar (0 without it)
  const setNotificationBarOffset = useSetNotificationBarOffset()
  useEffect(() => {
    if (!showNotificationBar) setNotificationBarOffset(0)
  }, [showNotificationBar, setNotificationBarOffset])

  const handlePluginChange = (newPluginId) => {
    const pluginToShow = plugins[newPluginId]
    const defaultRoute = pluginToShow.getDefaultRoute()
    history.push(defaultRoute)
  }
  const frameValue = useMemo(
    () => ({
      ...frameRefs,
      setFrameContainerRef: setDefaultFrameContainerRef,
    }),
    [frameRefs, setDefaultFrameContainerRef],
  )

  return (
    <FrameContext.Provider value={frameValue}>
      <main
        className={clsx(
          classes.appFrame,
          sidebarPane === 'custom' && classes.appFrameCustom,
          showNotificationBar &&
            (sidebarPane === 'custom' ? classes.appFrameCustomWithBar : classes.appFrameWithBar),
        )}
      >
        <Header />
        <Sidebar setPluginId={handlePluginChange} />
        <MainContent plugins={plugins} role={role} sidebarPane={sidebarPane} />
        {showNotificationBar && (
          <div className={classes.notificationBar}>
            <NotificationBar onHeightChange={setNotificationBarOffset} />
          </div>
        )}
        {/* <section className={clsx('content-main', classes.contentMain)}>
          {renderMainContent(plugins, role)}
        </section> */}
      </main>
    </FrameContext.Provider>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  appFrame: {
    position: 'relative',
    display: 'grid',
    backgroundColor: theme.components.frame.background,
    width: '100vw',
    // APP-FR-001: Inside AuthContainer's shell it takes the height the banners leave; the window
    // height is the fallback elsewhere
    height: '100vh',
    flex: '1 1 0',
    minHeight: 0,
    maxWidth: '100vw',
    maxHeight: '100vh',
    gridTemplateRows: 'minmax(65px, max-content) 1fr',
    gridTemplateColumns: 'max-content 1fr',
    gridTemplateAreas: '"frame-nav frame-header" "frame-nav frame-content"',

    '& > header': {
      gridArea: 'frame-header',
      visibility: 'visible',
      position: 'inherit',
    },
    '& > aside': {
      gridArea: 'frame-nav',
      // APP-FR-001: The sidebar fills its rows instead of the window height, which would run past
      // a frame shortened by a banner
      height: 'auto',
    },
    '& > section': {
      gridArea: 'frame-content',
    },

    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: '100%',
      height: 1,
      backgroundColor: theme.components.sidebar.border,
    },
  },
  notificationBar: {
    gridArea: 'frame-bar',
    minWidth: 0,
    position: 'relative',
    // An open drawer covers the sidebar and the page (and their sticky or raised parts, up to
    // z-index 1000), but stays below dialogs and menus (1300)
    zIndex: 1050,
  },
  appFrameCustom: {
    gridTemplateAreas: '"frame-nav frame-content"',

    '& > header': {
      visibility: 'hidden',
      position: 'absolute',
    },
  },
  // NB-FR-001: The collapsed bar gets its own full-width row, so the page and the sidebar (with its
  // version text) end above it. Declared after appFrameCustom so these grid areas win over its own.
  appFrameWithBar: {
    gridTemplateRows: `minmax(65px, max-content) 1fr ${COLLAPSED_BAR_HEIGHT}px`,
    gridTemplateAreas: '"frame-nav frame-header" "frame-nav frame-content" "frame-bar frame-bar"',
  },
  appFrameCustomWithBar: {
    gridTemplateRows: `1fr ${COLLAPSED_BAR_HEIGHT}px`,
    gridTemplateAreas: '"frame-nav frame-content" "frame-bar frame-bar"',
  },
  contentMain: {
    display: 'grid',
    padding: theme.spacing(1, 4, 10, 4), //'8px 32px 80px 32px',
    overflow: 'auto',
    gridAutoRows: 'max-content',

    // FF supports these fields but not webkit
    scrollbarColor: `${theme?.components?.scrollbar?.thumb} ${theme?.components?.frame?.background}`,
    scrollbarWidth: 'thin',

    '@media screen and (max-width: 768px)': {
      // iOS fix for momentum scrolling
      '-webkit-overflow-scrolling': 'touch',
    },
  },
  secondaryHeader: {
    zIndex: 1100,
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerSpacing: {
    marginBottom: 16,
  },
  headerActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    gap: 16,
  },
}))

export default DefaultFrame
